// ============================================================================
// CrewAhead Maritime Platform - Generic useApi Hook
// ============================================================================

import {
  useQuery,
  useMutation,
  type UseQueryOptions,
  type UseMutationOptions,
  type QueryKey,
} from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { CrewAheadApiError } from '../lib/api/client';
import type { AxiosRequestConfig } from 'axios';

// ─── Types ───────────────────────────────────────────────────────────────────

interface QueryConfig<TData>
  extends Omit<UseQueryOptions<TData, CrewAheadApiError, TData, QueryKey>, 'queryKey' | 'queryFn'> {
  /** Query key for caching */
  queryKey: QueryKey;
  /** URL path (relative to baseURL) */
  url: string;
  /** Whether to disable the query (e.g., when params are not yet ready) */
  enabled?: boolean;
  /** Additional axios config */
  axiosConfig?: AxiosRequestConfig;
}

interface MutationConfig<TData, TVariables = unknown>
  extends Omit<UseMutationOptions<TData, CrewAheadApiError, TVariables>, 'mutationFn'> {
  /** URL path (relative to baseURL) */
  url: string;
  /** HTTP method */
  method?: 'post' | 'put' | 'patch' | 'delete';
  /** Additional axios config */
  axiosConfig?: AxiosRequestConfig;
}

// ─── useApiQuery ─────────────────────────────────────────────────────────────

/**
 * Generic hook for making GET API calls using TanStack Query.
 *
 * @param config - Query configuration
 * @returns Query result with data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useApiQuery({
 *   queryKey: ['vessel', vesselId],
 *   url: `/vessels/${vesselId}`,
 *   enabled: !!vesselId,
 * });
 * ```
 */
export function useApiQuery<TData = unknown>(config: QueryConfig<TData>) {
  const { queryKey, url, enabled = true, axiosConfig, ...queryOptions } = config;

  return useQuery<TData, CrewAheadApiError, TData, QueryKey>({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.get<TData>(url, axiosConfig);
      return response.data;
    },
    enabled,
    ...queryOptions,
  });
}

// ─── useApiMutation ──────────────────────────────────────────────────────────

/**
 * Generic hook for making mutating API calls (POST, PUT, PATCH, DELETE).
 *
 * @param config - Mutation configuration
 * @returns Mutation result with mutate function, loading state, and error
 *
 * @example
 * ```tsx
 * const { mutate, isPending, error } = useApiMutation({
 *   url: '/feedback',
 *   method: 'post',
 *   onSuccess: () => toast.success('Feedback sent!'),
 * });
 *
 * mutate({ message: 'Great platform!' });
 * ```
 */
export function useApiMutation<TData = unknown, TVariables = unknown>(
  config: MutationConfig<TData, TVariables>
) {
  const { url, method = 'post', axiosConfig, ...mutationOptions } = config;

  return useMutation<TData, CrewAheadApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await apiClient.request<TData>({
        method,
        url,
        data: variables,
        ...axiosConfig,
      });
      return response.data;
    },
    ...mutationOptions,
  });
}

// ─── Convenience: useGet ─────────────────────────────────────────────────────

/**
 * Convenience hook for simple GET requests with automatic query key generation.
 *
 * @param url - The API URL path
 * @param options - Optional query and axios configuration
 * @returns Query result
 *
 * @example
 * ```tsx
 * const { data: countries } = useGet<string[]>('/reference/countries');
 * ```
 */
export function useGet<TData = unknown>(
  url: string | null,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchInterval?: number | false;
    axiosConfig?: AxiosRequestConfig;
  }
) {
  const queryKey = url ? ['api', 'get', url] : ['api', 'get', 'null'];

  return useQuery<TData, CrewAheadApiError>({
    queryKey,
    queryFn: async () => {
      if (!url) throw new Error('URL is required');
      const response = await apiClient.get<TData>(url, options?.axiosConfig);
      return response.data;
    },
    enabled: options?.enabled !== false && !!url,
    staleTime: options?.staleTime ?? 1000 * 60 * 5,
    refetchInterval: options?.refetchInterval ?? false,
  });
}

// ─── Convenience: usePost ────────────────────────────────────────────────────

/**
 * Convenience hook for POST requests.
 *
 * @param url - The API URL path
 * @param options - Optional mutation configuration
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * const { mutate, isPending } = usePost('/contact', {
 *   onSuccess: () => toast.success('Message sent!'),
 * });
 *
 * mutate({ name: 'John', message: 'Hello' });
 * ```
 */
export function usePost<TData = unknown, TVariables = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, CrewAheadApiError, TVariables>, 'mutationFn'> & {
    axiosConfig?: AxiosRequestConfig;
  }
) {
  const { axiosConfig, ...mutationOptions } = options ?? {};

  return useMutation<TData, CrewAheadApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await apiClient.post<TData>(url, variables, axiosConfig);
      return response.data;
    },
    ...mutationOptions,
  });
}

// ─── Convenience: usePut ─────────────────────────────────────────────────────

/**
 * Convenience hook for PUT requests.
 *
 * @param url - The API URL path (can include template params)
 * @param options - Optional mutation configuration
 * @returns Mutation result
 */
export function usePut<TData = unknown, TVariables = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, CrewAheadApiError, TVariables>, 'mutationFn'> & {
    axiosConfig?: AxiosRequestConfig;
  }
) {
  const { axiosConfig, ...mutationOptions } = options ?? {};

  return useMutation<TData, CrewAheadApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await apiClient.put<TData>(url, variables, axiosConfig);
      return response.data;
    },
    ...mutationOptions,
  });
}

// ─── Convenience: usePatch ───────────────────────────────────────────────────

/**
 * Convenience hook for PATCH requests.
 *
 * @param url - The API URL path (can include template params)
 * @param options - Optional mutation configuration
 * @returns Mutation result
 */
export function usePatch<TData = unknown, TVariables = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, CrewAheadApiError, TVariables>, 'mutationFn'> & {
    axiosConfig?: AxiosRequestConfig;
  }
) {
  const { axiosConfig, ...mutationOptions } = options ?? {};

  return useMutation<TData, CrewAheadApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await apiClient.patch<TData>(url, variables, axiosConfig);
      return response.data;
    },
    ...mutationOptions,
  });
}

// ─── Convenience: useDelete ──────────────────────────────────────────────────

/**
 * Convenience hook for DELETE requests.
 *
 * @param url - The API URL path (can include template params like :id)
 * @param options - Optional mutation configuration
 * @returns Mutation result where variables are used to build the URL
 *
 * @example
 * ```tsx
 * const { mutate: deleteItem } = useDelete('/items/:id', {
 *   urlBuilder: (id) => `/items/${id}`,
 * });
 *
 * deleteItem('123');
 * ```
 */
export function useDelete<TData = unknown, TVariables = string>(
  urlOrUrlBuilder: string | ((variables: TVariables) => string),
  options?: Omit<UseMutationOptions<TData, CrewAheadApiError, TVariables>, 'mutationFn'> & {
    axiosConfig?: AxiosRequestConfig;
  }
) {
  const { axiosConfig, ...mutationOptions } = options ?? {};

  return useMutation<TData, CrewAheadApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const url =
        typeof urlOrUrlBuilder === 'function'
          ? urlOrUrlBuilder(variables)
          : urlOrUrlBuilder;
      const response = await apiClient.delete<TData>(url, axiosConfig);
      return response.data;
    },
    ...mutationOptions,
  });
}

// ─── Default Export ──────────────────────────────────────────────────────────

export interface UseApiReturn {
  /** Generic GET query hook */
  useApiQuery: typeof useApiQuery;
  /** Generic mutation hook */
  useApiMutation: typeof useApiMutation;
  /** Simple GET hook */
  useGet: typeof useGet;
  /** Simple POST hook */
  usePost: typeof usePost;
  /** Simple PUT hook */
  usePut: typeof usePut;
  /** Simple PATCH hook */
  usePatch: typeof usePatch;
  /** Simple DELETE hook */
  useDelete: typeof useDelete;
}

/**
 * Combined hook that exposes all generic API hooks.
 *
 * @example
 * ```tsx
 * const { useApiQuery, useApiMutation, useGet, usePost } = useApi();
 * ```
 */
export function useApi(): UseApiReturn {
  return {
    useApiQuery,
    useApiMutation,
    useGet,
    usePost,
    usePut,
    usePatch,
    useDelete,
  };
}
